import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, Menu, X } from "lucide-react";
import { trackPhoneConversion } from "../utils/tracking";


const PHONE = "+359876995651";
const PHONE_DISPLAY = "+359 876 995 651";
const FB_URL = "https://www.facebook.com/profile.php?id=61577170304687";

const navLinks = [
  { to: "/", label: "Начало" },
  { to: "/hamalski-uslugi", label: "Хамалски услуги" },
  { to: "/transportni-uslugi", label: "Транспорт" },
  { to: "/ofis-hamali", label: "Офис хамали" },
  { to: "/pochistvane", label: "Почистване" },
  { to: "/kontakti", label: "Контакти" },
];

const FacebookIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
  </svg>
);

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      <header
        data-testid="navbar"
        style={{
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
          backgroundColor: scrolled ? "rgba(10,10,10,0.97)" : "rgba(10,10,10,0.92)",
          backdropFilter: "blur(16px)",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
          transition: "background-color 0.3s ease",
        }}
      >
        <div style={{ maxWidth: "1280px", margin: "0 auto", padding: "0 1.25rem" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: "76px" }}>

            {/* Logo */}
            <Link to="/" data-testid="navbar-logo" style={{ display: "flex", alignItems: "center", textDecoration: "none", flexShrink: 0 }}>
              <img src="/logo.svg" alt="Хамали София" style={{ height: "60px", width: "auto", filter: "brightness(1.1)" }} />
            </Link>

            {/* Desktop Nav — ONLY inline gap/alignItems, Tailwind controls display */}
            <nav
              data-testid="desktop-nav"
              className="hidden lg:flex"
              style={{ alignItems: "center", gap: "1.75rem" }}
            >
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`nav-link ${location.pathname === link.to ? "active" : ""}`}
                  style={{
                    color: location.pathname === link.to ? "#2dcf0c" : "#d4d4d8",
                    textDecoration: "none",
                    fontSize: "0.875rem",
                    fontFamily: "Manrope, sans-serif",
                    fontWeight: 600,
                    letterSpacing: "0.02em",
                  }}
                  data-testid={`nav-link-${link.to.slice(1)}`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Desktop CTA — Tailwind hidden/inline-flex */}
            <a
              href={`tel:${PHONE}`}
              data-testid="navbar-call-btn"
              className="btn-call hidden lg:inline-flex"
              style={{ padding: "0.6rem 1.25rem", fontSize: "0.875rem", borderRadius: "4px", flexShrink: 0 }}
              onClick={trackPhoneConversion}
            >
              <Phone size={16} />
              <span>Обадете се</span>
            </a>

            {/* Mobile hamburger — visible only on mobile */}
            <button
              onClick={() => setOpen(!open)}
              data-testid="mobile-menu-toggle"
              aria-label={open ? "Затвори меню" : "Отвори меню"}
              className="flex lg:hidden"
              style={{
                background: "none",
                border: "1px solid rgba(255,255,255,0.15)",
                color: "#fff",
                cursor: "pointer",
                padding: "8px",
                borderRadius: "6px",
                alignItems: "center",
                justifyContent: "center",
                transition: "border-color 0.2s ease",
              }}
            >
              {open ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Fullscreen Overlay */}
      <div
        data-testid="mobile-menu"
        style={{
          position: "fixed",
          top: "76px",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 99,
          backgroundColor: "#080808",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          padding: "1.5rem 1.5rem 2rem",
          gap: "0",
          overflowY: "auto",
          pointerEvents: open ? "auto" : "none",
          opacity: open ? 1 : 0,
          transform: open ? "translateY(0)" : "translateY(-8px)",
          transition: "opacity 0.25s ease, transform 0.25s ease",
        }}
      >
        {/* Nav links */}
        <nav style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0", width: "100%", marginBottom: "1.25rem" }}>
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              data-testid={`mobile-nav-${link.to.slice(1)}`}
              style={{
                color: location.pathname === link.to ? "#2dcf0c" : "#FFFFFF",
                textDecoration: "none",
                fontSize: "clamp(1.25rem, 5vw, 1.75rem)",
                fontFamily: "Montserrat, sans-serif",
                fontWeight: 800,
                letterSpacing: "-0.02em",
                textTransform: "uppercase",
                transition: "color 0.2s ease",
                padding: "0.75rem 1rem",
                width: "100%",
                textAlign: "center",
                borderBottom: "1px solid rgba(255,255,255,0.06)",
              }}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* Call button */}
        <a
          href={`tel:${PHONE}`}
          data-testid="mobile-nav-call-btn"
          className="btn-call"
          style={{
            padding: "1rem 2rem",
            fontSize: "1.05rem",
            borderRadius: "6px",
            width: "100%",
            maxWidth: "340px",
            marginBottom: "1.25rem",
          }}
          onClick={trackPhoneConversion}
        >
          <Phone size={20} />
          <span>Обадете се</span>
        </a>

        {/* Facebook link */}
        <a
          href={FB_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="mobile-nav-facebook"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.625rem",
            color: "#A1A1AA",
            textDecoration: "none",
            fontSize: "0.9rem",
            fontFamily: "Manrope, sans-serif",
            fontWeight: 600,
            padding: "0.75rem 1rem",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: "6px",
            width: "100%",
            maxWidth: "340px",
            justifyContent: "center",
            transition: "color 0.2s ease, border-color 0.2s ease",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = "#1877F2"; e.currentTarget.style.borderColor = "rgba(24,119,242,0.4)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = "#A1A1AA"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"; }}
        >
          <FacebookIcon />
          <span>Facebook страница</span>
        </a>

        {/* Close hint */}
        <button
          onClick={() => setOpen(false)}
          style={{
            background: "none",
            border: "none",
            color: "#52525B",
            cursor: "pointer",
            marginTop: "2rem",
            fontSize: "0.8rem",
            fontFamily: "Manrope, sans-serif",
            display: "flex",
            alignItems: "center",
            gap: "0.375rem",
          }}
        >
          <X size={14} />
          Затвори
        </button>
      </div>
    </>
  );
}
